import React, { useEffect, useState } from "react";
import { Link2, Trash2, Eye, Users, ChevronDown, Package } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const fmt = (s) => (s >= 3600 ? `${Math.floor(s / 3600)}h ${Math.round((s % 3600) / 60)}m` : s >= 60 ? `${Math.round(s / 60)} min` : s > 0 ? `${s}s` : "—");

const Part = ({ p, i, pid }) => (
  <div className="rounded-lg bg-white/[0.03] border border-white/5 px-3 py-2" data-testid={`pack-part-${pid}-${i}`}>
    <div className="flex items-center justify-between gap-2 text-xs">
      <span className="truncate"><span className="font-semibold">{i + 1}. {p.title}</span><span className="text-slate-500"> · {p.section}</span></span>
      <span className="font-tab text-[#00f0ff] shrink-0" data-testid={`pack-part-total-${pid}-${i}`}>{fmt(p.total_seconds)}</span>
    </div>
    {(p.students.length > 0 || p.guest_seconds > 0) && (
      <div className="mt-1.5 flex flex-wrap gap-1.5">
        {p.students.map((st) => (
          <span key={st.name} className="text-[10px] rounded-full bg-[#ffab00]/10 text-[#ffab00] px-2 py-0.5">{st.name} · {fmt(st.seconds)}</span>
        ))}
        {p.guest_seconds > 0 && <span className="text-[10px] rounded-full bg-white/5 text-slate-400 px-2 py-0.5">Guests · {fmt(p.guest_seconds)}</span>}
      </div>
    )}
  </div>
);

const PackCard = ({ pk, onDelete }) => {
  const [open, setOpen] = useState(false);
  const url = `${window.location.origin}/p/${pk.id}`;
  const total = pk.parts.reduce((a, p) => a + p.total_seconds, 0);
  const students = new Set(pk.parts.flatMap((p) => p.students.map((s) => s.name))).size;
  const copy = async () => {
    try { await navigator.clipboard.writeText(url); toast.success("Pack link copied"); } catch (e) { toast.message(url); }
  };
  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4" data-testid={`my-pack-${pk.id}`}>
      <div className="flex items-start justify-between gap-3">
        <button onClick={() => setOpen((o) => !o)} className="min-w-0 text-left flex-1" data-testid={`my-pack-toggle-${pk.id}`}>
          <p className="font-semibold truncate">{pk.caption || "Practice pack"}</p>
          <p className="text-xs text-slate-500 mt-0.5">
            {new Date(pk.created_at).toLocaleDateString()} · {pk.parts.length} part{pk.parts.length > 1 ? "s" : ""}
          </p>
          <div className="flex flex-wrap items-center gap-3 mt-2 text-[11px] font-tab text-slate-300">
            <span className="inline-flex items-center gap-1" data-testid={`my-pack-views-${pk.id}`}><Eye className="w-3.5 h-3.5" /> {pk.views} opens</span>
            <span className="inline-flex items-center gap-1"><Users className="w-3.5 h-3.5" /> {students} student{students === 1 ? "" : "s"}</span>
            <span className="text-[#00f0ff]" data-testid={`my-pack-total-${pk.id}`}>{fmt(total)} practised</span>
            <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
          </div>
        </button>
        <div className="flex gap-1 shrink-0">
          <button onClick={copy} className="w-8 h-8 rounded-full border border-white/15 inline-flex items-center justify-center hover:border-[#00f0ff]/50 hover:text-[#00f0ff] transition-colors" title="Copy link" data-testid={`my-pack-copy-${pk.id}`}><Link2 className="w-4 h-4" /></button>
          <button onClick={() => onDelete(pk.id)} className="w-8 h-8 rounded-full border border-white/15 inline-flex items-center justify-center hover:border-red-400/50 hover:text-red-400 transition-colors" title="Delete pack" data-testid={`my-pack-delete-${pk.id}`}><Trash2 className="w-4 h-4" /></button>
        </div>
      </div>
      {open && <div className="mt-3 flex flex-col gap-1.5">{pk.parts.map((p, i) => <Part key={i} p={p} i={i} pid={pk.id} />)}</div>}
    </div>
  );
};

export const MyPacks = () => {
  const [packs, setPacks] = useState(null);
  useEffect(() => {
    api.get("/my-packs").then((r) => setPacks(r.data.packs)).catch(() => setPacks([]));
  }, []);
  const del = async (id) => {
    if (!window.confirm("Delete this practice pack? Students will no longer be able to open the link.")) return;
    try {
      await api.delete(`/packs/${id}`);
      setPacks((ps) => ps.filter((p) => p.id !== id));
      toast.success("Practice pack deleted");
    } catch (e) {
      toast.error("Could not delete the pack");
    }
  };
  if (!packs) return <p className="text-center text-sm text-slate-500 py-10">Loading…</p>;
  if (!packs.length) {
    return (
      <div className="text-center py-12 text-slate-500" data-testid="my-packs-empty">
        <Package className="w-10 h-10 mx-auto mb-3 opacity-40" />
        <p className="text-sm">No practice packs yet. Use "Student clip pack" → "Share as practice pack" to make one.</p>
      </div>
    );
  }
  return (
    <div className="flex flex-col gap-3" data-testid="my-packs">
      <p className="text-[11px] text-slate-500">Practice time counts while a part is playing. Logged-in students appear by name; others are counted as guests.</p>
      {packs.map((pk) => <PackCard key={pk.id} pk={pk} onDelete={del} />)}
    </div>
  );
};
